/* Optional, heading-aware rods and tracks. Prices remain native Shopify product prices. */
(() => {
  const models = [
    ['kalia-rex-custom-traverse-rods', 'Rex', 'Traverse rod · straight, corner or bay'],
    ['kalia-wesley-custom-curtain-tracks', 'Wesley', 'Manual curtain track'],
    ['kalia-john-custom-curtain-rods', 'John', 'Decorative rod · choose your finial'],
    ['kalia-max-custom-french-return-curtain-rod', 'Max', 'French return rod · up to 82 inches'],
    ['kalia-jason-custom-motorized-curtain-track', 'Jason', 'Motorized curtain track']
  ];
  const backTab = heading => /back\s*tab/i.test(String(heading || ''));
  const supported = heading => /pleat|goblet/i.test(String(heading || ''));
  const number = value => String(value ?? '').trim() === '' ? NaN : Number(value);
  // Exact centimetres are retained independently of the unit currently on screen.
  const cmText = micros => Number.isSafeInteger(micros) ? (micros / 1000000).toFixed(6).replace(/\.?0+$/, '') : '';
  const cmDisplay = micros => Number.isSafeInteger(micros) ? (micros / 1000000).toFixed(1) : '';
  const readMeasurement = (raw, fraction, unit, previous, fractional) => {
    raw = String(raw ?? '').trim(); fraction = Number(fraction || 0);
    if (previous?.displayUnit === unit && previous.displayRaw === raw && previous.displayFraction === fraction) return previous;
    let error = '';
    if (unit === 'cm' && raw && !/^\d+(?:\.\d{0,6})?$/.test(raw)) error = 'Enter centimetres with up to 6 decimal places.';
    if (unit !== 'cm' && fractional && raw && !Number.isInteger(Number(raw))) error = 'Enter whole inches and select the remaining fraction separately.';
    const value = raw === '' ? NaN : Number(raw) + (unit === 'cm' ? 0 : fraction);
    const scaled = value * (unit === 'cm' ? 1000000 : 2540000);
    const cmMicros = Number.isFinite(value) ? Math.round(scaled) : NaN;
    if (unit !== 'cm' && Number.isFinite(value) && Math.abs(scaled - cmMicros) > 0.00001) error = 'Use the supported inch increments. Measurements are not rounded.';
    if (Number.isFinite(value) && !Number.isSafeInteger(cmMicros)) error = 'Enter a valid hardware measurement.';
    return { cmMicros, sourceUnit: unit, sourceRaw: raw, sourceFraction: fraction, displayUnit: unit, displayRaw: raw, displayFraction: fraction, error };
  };
  const displayMeasurement = (record, unit, fractional) => {
    if (!record) return { raw: '', fraction: 0 };
    if (unit === 'cm' && Number.isSafeInteger(record.cmMicros)) return { raw: cmDisplay(record.cmMicros), fraction: 0 };
    if (unit === record.sourceUnit) return { raw: record.sourceRaw, fraction: record.sourceFraction };
    if (!Number.isSafeInteger(record.cmMicros)) return { raw: record.sourceRaw || '', fraction: 0 };
    const inches = record.cmMicros / 2540000;
    return fractional ? { raw: String(Math.floor(inches)), fraction: inches - Math.floor(inches) } : { raw: String(Number(inches.toFixed(12))), fraction: 0 };
  };
  const sourceMeasurement = record => {
    const fractions = { 0.125: '1/8', 0.25: '1/4', 0.375: '3/8', 0.5: '1/2', 0.625: '5/8', 0.75: '3/4', 0.875: '7/8' };
    return record?.sourceRaw ? `${record.sourceRaw}${record.sourceUnit !== 'cm' && record.sourceFraction ? ` ${fractions[record.sourceFraction] || record.sourceFraction}` : ''} ${record.sourceUnit === 'cm' ? 'cm' : 'in'}` : '';
  };
  const imageUrl = value => { try { const url = new URL(value, location.origin); return url.protocol === 'https:' ? url.href : ''; } catch (_) { return ''; } };
  const emptyQuote = () => ({ selected: false, totalCents: 0, items: [], summary: [] });
  const create = (tag, className, text) => { const el = document.createElement(tag); if (className) el.className = className; if (text !== undefined) el.textContent = text; return el; };
  let instanceCount = 0;

  class KaliaDraperyHardware {
    constructor(host, options = {}) {
      if (!host) throw new Error('A hardware options container is required.');
      this.host = host;
      this.settings = options;
      this.money = options.money || (cents => new Intl.NumberFormat(document.documentElement.lang || 'en', { style: 'currency', currency: 'USD' }).format(cents / 100));
      this.context = {};
      this.selection = { selected: false, handle: '', color: '', options: {}, dimensions: [], quantity: 1, confirmed: false };
      this.revision = 0;
      this.cache = new Map();
      this.prefix = `kdh-${++instanceCount}`;
      const root = new URL(options.rootUrl || '/', location.origin);
      if (root.origin !== location.origin) throw new Error('Hardware must use this storefront.');
      this.rootUrl = root.pathname.replace(/\/?$/, '/');
    }

    static compatibleModels(heading) {
      const label = typeof heading === 'object' ? heading?.label : heading;
      if (!supported(label)) return [];
      return models.filter(([handle]) => !backTab(label) || /kalia-(john|max)-/.test(handle)).map(([handle]) => handle);
    }

    async json(url, fresh = false) {
      if (!url) throw new Error('Hardware information is not available. Please contact Design Help.');
      if (!fresh && this.cache.has(url)) return this.cache.get(url);
      const pending = fetch(url, { credentials: 'same-origin', cache: fresh ? 'no-store' : 'default', headers: { Accept: 'application/json' } }).then(async response => {
        if (!response.ok) throw new Error('Hardware price or availability could not be confirmed. Please try again.');
        return response.json();
      });
      if (!fresh) { this.cache.set(url, pending); pending.catch(() => this.cache.delete(url)); }
      return pending;
    }

    async initialize() {
      if (this.initializing) return this.initializing;
      this.renderShell();
      this.initializing = (async () => {
        try {
          const [catalog, custom, index, pricing] = await Promise.all([
            this.json(this.settings.configUrl),
            this.settings.customConfigUrl ? this.json(this.settings.customConfigUrl) : Promise.resolve(null),
            this.json(this.settings.priceIndexUrl),
            this.settings.pricing ? Promise.resolve(this.settings.pricing) : import(this.settings.pricingUrl)
          ]);
          if (this.destroyed) return;
          if (catalog.currency !== 'USD' || index.currency !== 'USD' || typeof pricing.quoteHardware !== 'function') throw new Error('Hardware configuration is not available in this currency.');
          this.catalog = catalog;
          this.priceIndex = index;
          this.pricing = pricing;
          this.products = new Map(models.map(([handle]) => {
            const product = catalog.products?.[handle];
            const configuration = product?.configuration || custom?.products?.find(item => item.handle === product?.sourceHandle);
            if (!product || product.kind !== 'custom' || !configuration || !product.nativeSourceVariants?.length) throw new Error('Matching hardware information needs an update. Contact Design Help.');
            return [handle, { ...product, configuration }];
          }));
          this.initialized = true;
          this.enableInput.disabled = this.disabled === true;
          this.renderModels();
          this.setDisabled(this.disabled === true);
          await this.refresh();
        } catch (error) {
          this.enableInput.disabled = true;
          if (!this.destroyed) { this.status.textContent = `${error.message} You can continue with your own hardware.`; this.notify({ selected: false, ready: true, loading: false, totalCents: 0, summary: [], error: '' }); }
          throw error;
        }
      })();
      return this.initializing;
    }

    renderShell() {
      const fieldset = create('fieldset', 'kdh-module');
      fieldset.append(create('legend', '', 'Complete your setup (optional)'));
      const toggle = create('label', 'kdh-toggle');
      this.enableInput = create('input'); this.enableInput.type = 'checkbox'; this.enableInput.disabled = true;
      this.enableInput.addEventListener('change', () => {
        this.selection.selected = this.enableInput.checked;
        this.selection.confirmed = false;
        this.revision++;
        this.body.hidden = !this.selection.selected;
        this.setDisabled(this.disabled === true);
        this.refresh();
      });
      toggle.append(this.enableInput, create('span', '', 'Add a matching rod or track'));
      fieldset.append(toggle, create('p', 'kdh-own-note', 'Already have hardware? Leave this off and order your drapery on its own.'));
      this.body = create('div', 'kdh-body'); this.body.hidden = true;
      this.guide = create('p', 'kdh-guidance');
      this.modelCards = create('div', 'kdh-models'); this.modelCards.setAttribute('role', 'group'); this.modelCards.setAttribute('aria-label', 'Compatible rod and track models');
      this.controls = create('div', 'kdh-controls');
      this.body.append(this.guide, this.modelCards, this.controls);
      this.price = create('p', 'kdh-price');
      this.status = create('p', 'kdh-status', 'Loading optional hardware…'); this.status.setAttribute('role', 'status'); this.status.setAttribute('aria-live', 'polite');
      const catalog = create('a', 'kdh-catalog-link', 'Explore all hardware'); catalog.href = this.rootUrl + 'collections/curtain-hardware';
      fieldset.append(this.body, this.price, this.status, catalog);
      this.host.replaceChildren(fieldset);
      this.fieldset = fieldset;
      this.notify({ selected: false, ready: true, loading: true, totalCents: 0, summary: [], error: '' });
      this.updateGuidance();
    }

    setContext(context = {}) {
      const heading = typeof context.heading === 'object' ? context.heading?.label : context.heading;
      const next = { ...context, heading: String(heading || '') };
      const prior = this.context;
      const changed = ['heading', 'width', 'length', 'quantity'].some(key => prior[key] !== next[key]);
      this.context = next;
      if (changed) {
        this.revision++;
        this.selection.confirmed = false;
        // Panel dimensions and panel count never populate hardware measurements or quantity.
        if (this.initialized && this.selection.handle && !KaliaDraperyHardware.compatibleModels(next.heading).includes(this.selection.handle)) this.clearModel();
        else if (prior.heading !== next.heading && this.selection.handle) this.sanitizeOptions();
        if (this.initialized) {
          this.renderModels(); this.renderControls();
          this.price.textContent = '';
          this.status.textContent = this.selection.selected ? 'Review the hardware setup for your current drapery measurements and heading.' : 'Hardware is optional and priced separately.';
        }
      }
      else if (prior.room !== next.room) this.revision++;
      this.updateGuidance();
    }

    updateGuidance() {
      if (!this.guide) return;
      this.guide.textContent = backTab(this.context.heading)
        ? 'For hanging by back tabs, choose John or Max without rings. Check tab clearance and rod diameter. To hang by pleat hooks on a track, choose a pleated heading without Back Tab.'
        : supported(this.context.heading)
          ? 'For this heading, choose pleat runners on a track or compatible rings and hooks on a rod. Rods and tracks are separate from your fabric panels.'
          : 'Choose your drapery heading first to see compatible quick setup options.';
      if (Number.isFinite(this.context.width) && Number.isInteger(this.context.quantity) && this.context.quantity > 0) {
        this.guide.textContent += ` Your ${this.context.quantity} panels have ${this.context.width * this.context.quantity} inches of total finished width. Plan hardware coverage separately, including any overlap, returns and stacking space.`;
      }
    }

    renderModels() {
      if (!this.modelCards || !this.products) return;
      const allowed = KaliaDraperyHardware.compatibleModels(this.context.heading);
      this.modelCards.replaceChildren();
      models.filter(([handle]) => allowed.includes(handle)).forEach(([handle, title, description]) => {
        const product = this.products.get(handle);
        const button = create('button', 'kdh-model'); button.type = 'button'; button.disabled = this.disabled === true || !this.selection.selected;
        button.setAttribute('aria-pressed', String(this.selection.handle === handle));
        const src = imageUrl(product.nativeSourceVariants[0].image);
        if (src) { const img = create('img'); img.src = src; img.alt = product.title; img.loading = 'lazy'; img.width = 160; img.height = 120; button.append(img); }
        button.append(create('strong', '', title), create('span', '', description));
        button.addEventListener('click', () => this.chooseModel(handle));
        this.modelCards.append(button);
      });
    }

    clearModel() { this.selection = { selected: this.selection.selected, handle: '', color: '', options: {}, dimensions: [], quantity: 1, confirmed: false }; }

    chooseModel(handle) {
      if (this.disabled || !this.products?.has(handle) || !KaliaDraperyHardware.compatibleModels(this.context.heading).includes(handle)) return;
      this.clearModel();
      this.selection.handle = handle;
      const product = this.products.get(handle), finishes = product.configuration.sourceVariants.filter(variant => variant.available);
      if (finishes.length === 1) this.selection.color = finishes[0].color;
      this.sanitizeOptions();
      this.revision++;
      this.renderModels(); this.renderControls(); this.refresh();
    }

    allowedValues(group) {
      if (group.id === 'runner_type') return group.values.filter(value => value.id === 'pinch_pleat_runners' || value.id === 'rings');
      if (group.id === 'ring_option' && backTab(this.context.heading)) return group.values.filter(value => value.id === 'john_no_rings');
      return group.values;
    }

    sanitizeOptions() {
      const product = this.products.get(this.selection.handle);
      if (!product) return;
      for (const group of product.configuration.optionGroups) {
        const values = this.allowedValues(group);
        if (!values.some(value => value.id === this.selection.options[group.id])) this.selection.options[group.id] = values.length === 1 ? values[0].id : '';
      }
    }

    field(label, input, note) {
      const wrap = create('div', 'kdh-field'), labelEl = create('label', '', label);
      input.id = `${this.prefix}-${this.controlCount++}`;
      input.name = input.id;
      labelEl.htmlFor = input.id;
      wrap.append(labelEl, input);
      if (note) { const help = create('p', 'kdh-field-note', note); help.id = input.id + '-help'; input.setAttribute('aria-describedby', help.id); wrap.append(help); }
      return wrap;
    }

    choice(name, label, values, value, action, note) {
      const visuals = window.KaliaHardwareOptionVisuals;
      let cards;
      if (typeof visuals?.content === 'function') {
        try {
          cards = values.map(option => visuals.content({ productHandle: this.selection.handle, name, id: option.id, label: option.label, image: option.image }));
          if (cards.some(card => !card || typeof card !== 'object')) cards = null;
        } catch (_) { cards = null; }
      }
      if (cards) {
        const fieldset = create('fieldset', 'kdh-field kdh-option-fieldset');
        fieldset.id = `${this.prefix}-${this.controlCount++}`;
        fieldset.append(create('legend', '', label));
        const choices = create('div', 'kdh-option-choices');
        const inputs = values.map((option, index) => {
          const choice = create('label', 'kdh-option-choice');
          const input = create('input'); input.type = 'radio';
          input.name = `${this.prefix}-choice-${name}`;
          input.id = `${fieldset.id}-${index}`;
          input.value = option.id;
          input.checked = option.id === value;
          input.kdhOptionUnavailable = option.disabled === true;
          input.disabled = input.kdhOptionUnavailable || this.disabled === true || !this.selection.selected;
          input.setAttribute('aria-label', option.label);
          input.addEventListener('change', () => {
            if (!input.checked || input.disabled) return;
            action(input.value);
            this.changed(true, { name, value: input.value });
          });
          choice.append(input, cards[index]); choices.append(choice);
          return input;
        });
        this.choiceControls?.set(name, inputs);
        fieldset.append(choices);
        if (note) {
          const help = create('p', 'kdh-field-note', note); help.id = fieldset.id + '-help';
          fieldset.setAttribute('aria-describedby', help.id);
          inputs.forEach(input => input.setAttribute('aria-describedby', help.id));
          fieldset.append(help);
        }
        return fieldset;
      }
      const select = create('select');
      const placeholder = create('option', '', `Choose ${label.toLowerCase()}`); placeholder.value = ''; select.append(placeholder);
      values.forEach(option => { const item = create('option', '', option.label); item.value = option.id; item.disabled = option.disabled === true; select.append(item); });
      select.value = value || '';
      select.addEventListener('change', () => {
        if (select.disabled || !this.selection.selected) return;
        action(select.value); this.changed(true, { name, value: select.value });
      });
      this.choiceControls?.set(name, [select]);
      return this.field(label, select, note);
    }

    renderControls() {
      if (!this.controls) return;
      this.controls.replaceChildren(); this.controlCount = 0; this.choiceControls = new Map();
      const product = this.products?.get(this.selection.handle);
      if (!product) return;
      const config = product.configuration, dimension = config.dimension;
      this.controls.append(this.choice('finish', 'Finish', config.sourceVariants.map(variant => ({ id: variant.color, label: variant.color, disabled: !variant.available, image: product.nativeSourceVariants.find(source => source.options?.Color === variant.color)?.image })), this.selection.color, value => { this.selection.color = value; }));
      const layout = config.optionGroups.find(group => group.id === 'width_type');
      if (layout) this.controls.append(this.choice(layout.id, layout.label, this.allowedValues(layout), this.selection.options[layout.id], value => { this.selection.options[layout.id] = value; }));
      const range = dimension.maximumIsKaliaOnlineLimit ? `Kalia online range: ${dimension.minimumInches}–${dimension.maximumInches} inches. Larger projects: Design Help.` : `Available range: ${dimension.minimumInches}–${dimension.maximumInches} inches.`;
      const measurements = create('div', 'kdh-measurements');
      const unit = this.selection.measurementUnit || 'inches';
      this.selection.measurementRecords = this.selection.measurementRecords || [];
      const units = create('div', 'kdh-measure-units'); units.setAttribute('role', 'group'); units.setAttribute('aria-label', 'Hardware measurement unit');
      ['inches', 'cm'].forEach(value => {
        const button = create('button', 'kdh-measure-unit', value === 'cm' ? 'cm' : 'inches'); button.type = 'button'; button.setAttribute('aria-pressed', String(value === unit));
        button.addEventListener('click', () => { if (!this.disabled && this.selection.selected && value !== unit) this.changeMeasurementUnit(value); }); units.append(button);
      });
      measurements.append(units);
      measurements.append(create('p', 'kdh-measure-note', `Enter the finished hardware width separately. Do not use one panel's width as the rod or track width. ${range}`));
      measurements.append(create('p', 'kdh-measure-note', `This model is made in ${dimension.stepInches}-inch increments (${cmText(Math.round(dimension.stepInches * 2540000))} cm). Enter an exact equivalent in cm; unsupported sizes need Design Help. Factory measurements are recorded in cm.`));
      const count = dimension.inputMode === 'segments' ? layout.values.find(value => value.id === this.selection.options.width_type)?.segments || 0 : 1;
      for (let index = 0; index < count; index++) {
        const input = create('input'); input.type = 'number'; input.min = unit === 'cm' ? cmDisplay(Math.round(dimension.minimumInches * 2540000)) : String(dimension.minimumInches); input.max = unit === 'cm' ? cmDisplay(Math.round(dimension.maximumInches * 2540000)) : String(dimension.maximumInches); input.step = unit === 'cm' ? '0.1' : String(dimension.stepInches); input.inputMode = 'decimal'; input.placeholder = unit === 'cm' ? 'Enter cm' : 'Enter inches'; input.autocomplete = 'off';
        input.value = this.selection.dimensions[index] ?? '';
        input.addEventListener('input', () => { this.editMeasurement(index, input.value); });
        measurements.append(this.field(dimension.inputMode === 'segments' ? `${['X', 'Y', 'Z'][index]} coverage width (${unit})` : `Finished hardware width (${unit})`, input));
      }
      if (dimension.inputMode === 'segments') measurements.append(create('p', 'kdh-field-note', count ? 'Enter active X / Y / Z segments from left to right. The range applies to the total. Use 0.1-inch increments.' : 'Choose normal, corner or bay window to enter the required segments.'));
      this.controls.append(measurements);
      for (const group of config.optionGroups.filter(group => group !== layout)) {
        const note = group.id === 'runner_type' ? 'Pleat/Flat Panel Runners match this pleated heading; ripple-fold runners are not offered here.' : group.id === 'ring_option' && !backTab(this.context.heading) ? 'Choose Silent Glide Rings for a hook-hung pleated panel. No Rings means you will supply compatible rings and hooks.' : '';
        this.controls.append(this.choice(group.id, group.label, this.allowedValues(group), this.selection.options[group.id], value => { this.selection.options[group.id] = value; }, note));
      }
      if (/kalia-max-/.test(product.handle)) this.controls.append(create('p', 'kdh-field-note', 'Max has 4.5-inch French returns per side. Fabric coverage is ordered rod width + 9 inches; this does not change the rod pricing width.'));
      if (/kalia-john-/.test(product.handle)) this.controls.append(create('p', 'kdh-field-note', 'John width is usable hanging space excluding finials. Inside Mount is limited to 70 inches; enter your opening width without making a deduction.'));
      const quantity = create('input'); quantity.type = 'number'; quantity.min = '1'; quantity.max = '1000'; quantity.step = '1'; quantity.inputMode = 'numeric'; quantity.value = String(this.selection.quantity);
      quantity.addEventListener('input', () => { this.selection.quantity = number(quantity.value); this.changed(false); });
      this.controls.append(this.field('Hardware quantity', quantity, 'One configured assembly per unit. This is separate from the number of curtain panels.'));
      const confirmation = create('label', 'kdh-confirm');
      this.confirmInput = create('input'); this.confirmInput.type = 'checkbox'; this.confirmInput.checked = this.selection.confirmed;
      this.confirmInput.addEventListener('change', () => { this.selection.confirmed = this.confirmInput.checked; this.revision++; this.refresh(); });
      confirmation.append(this.confirmInput, create('span', '', 'I have checked the hardware measurements, mounting surface, clearance, heading, required rings / hooks, curtain weight against hardware load capacity, and quantity.'));
      this.controls.append(confirmation);
      this.setDisabled(this.disabled === true);
    }

    changed(render = true, focusChoice) {
      this.selection.confirmed = false;
      if (this.confirmInput) this.confirmInput.checked = false;
      this.revision++;
      if (render) {
        this.renderControls();
        if (focusChoice && !this.disabled && this.selection.selected) {
          const input = this.choiceControls?.get(focusChoice.name)?.find(control => control.value === focusChoice.value && !control.disabled);
          input?.focus?.({ preventScroll: true });
          // Image zoom wraps new labels in a mutation-observer microtask. Moving
          // a focused label blurs its radio, so restore once after that work.
          const revision = this.revision;
          if (input && typeof window.requestAnimationFrame === 'function') window.requestAnimationFrame(() => {
            if (this.destroyed || this.disabled || !this.selection.selected || revision !== this.revision) return;
            const current = this.choiceControls?.get(focusChoice.name)?.find(control => control.value === focusChoice.value && !control.disabled);
            const active = document.activeElement;
            if (active && active !== document.body && active !== document.documentElement && active !== input && active !== current && active.isConnected !== false) return;
            current?.focus?.({ preventScroll: true });
          });
        }
      }
      this.refresh();
    }

    state() {
      const state = { ...this.selection, options: { ...this.selection.options }, color: this.selection.color, quantity: this.selection.quantity };
      const config = this.products?.get(state.handle)?.configuration;
      const unit = this.selection.measurementUnit || 'inches';
      const count = config?.dimension.inputMode === 'segments' ? config.optionGroups.find(item => item.id === 'width_type')?.values.find(item => item.id === state.options.width_type)?.segments || 0 : 1;
      this.selection.measurementRecords = this.selection.measurementRecords || [];
      state.measurements = this.selection.dimensions.slice(0, count).map((raw, index) => {
        const record = readMeasurement(raw, 0, unit, this.selection.measurementRecords[index], false);
        this.selection.measurementRecords[index] = record; return record;
      });
      state.measurementError = state.measurements.find(record => record.error)?.error || '';
      if (config?.dimension.inputMode === 'segments') {
        const group = config.optionGroups.find(item => item.id === 'width_type');
        const count = group.values.find(item => item.id === state.options.width_type)?.segments || 0;
        state.segmentsInches = state.measurements.map(record => record.cmMicros / 2540000);
      } else state.lengthInches = state.measurements[0]?.cmMicros / 2540000;
      delete state.dimensions;
      return state;
    }
    changeMeasurementUnit(unit) {
      const state = this.state();
      state.measurements.forEach((record, index) => {
        const value = displayMeasurement(record, unit, false);
        this.selection.dimensions[index] = value.raw;
        Object.assign(record, { displayUnit: unit, displayRaw: value.raw, displayFraction: 0 });
      });
      this.selection.measurementUnit = unit;
      this.changed(true);
    }
    editMeasurement(index, value) {
      this.selection.measurementRecords = this.selection.measurementRecords || [];
      this.selection.measurementRecords[index] = undefined;
      this.selection.dimensions[index] = value;
      this.changed(false);
    }

    summary(product, state, quote) {
      const rows = [['Hardware', product.title], ['Finish', state.color]];
      product.configuration.optionGroups.forEach(group => { const value = group.values.find(item => item.id === state.options[group.id]); if (value) rows.push([group.label, value.label]); });
      if (state.segmentsInches) rows.push(['Coverage segments', state.segmentsInches.map((value, index) => `${['X', 'Y', 'Z'][index]} ${value} in`).join(' · ')]);
      rows.push(['Hardware width', `${quote.lengthInches} in`], ['Hardware quantity', String(state.quantity)]);
      if (state.measurements?.every(record => Number.isSafeInteger(record.cmMicros))) rows.push(['Factory hardware width', `${cmDisplay(state.measurements.reduce((total, record) => total + record.cmMicros, 0))} cm`]);
      return rows;
    }

    async resolveQuote({ fresh = false, bundleId = '', room = '', requireConfirmation = true } = {}) {
      if (!this.selection.selected) return emptyQuote();
      if (!this.initialized) throw new Error('Wait for optional hardware to finish loading.');
      const revision = this.revision, state = this.state(), product = this.products.get(state.handle);
      if (state.measurementError) throw new Error(state.measurementError);
      if (!product || !KaliaDraperyHardware.compatibleModels(this.context.heading).includes(state.handle)) throw new Error('Choose a rod or track compatible with your drapery heading.');
      for (const group of product.configuration.optionGroups) {
        if (!this.allowedValues(group).some(value => value.id === state.options[group.id])) throw new Error(`Choose a compatible ${group.label.toLowerCase()}.`);
      }
      const quote = this.pricing.quoteHardware(product.configuration, state);
      if (!quote.ready) throw new Error(quote.errors[0] || 'Complete the hardware measurements and options.');
      if (!Number.isSafeInteger(quote.unitCents) || quote.unitCents <= 0 || !Number.isSafeInteger(quote.totalCents)) throw new Error('Hardware price could not be confirmed.');
      if (requireConfirmation && !state.confirmed) throw new Error('Confirm your hardware measurements and fit before adding the set.');
      const source = product.nativeSourceVariants.find(variant => variant.options?.Color === state.color);
      const provenance = product.configuration.sourceVariants.find(variant => variant.color === state.color && variant.available);
      if (!source || !provenance || source.sourceSku !== provenance.sku || String(source.sourceVariantId) !== String(provenance.id) || source.priceCents !== provenance.priceCents) throw new Error('This hardware finish needs a pricing update. Contact Design Help.');
      const shards = this.priceIndex.shards || this.priceIndex.products || this.priceIndex;
      const shard = Array.isArray(shards) && shards.find(item => quote.unitCents >= item.minCents && quote.unitCents <= item.maxCents);
      if (!shard?.handle) throw new Error('This hardware specification needs a team quote. Contact Design Help.');
      const [native, carrier] = await Promise.all([
        this.json(this.rootUrl + `products/${encodeURIComponent(product.handle)}.js`, fresh),
        this.json(this.rootUrl + `products/${encodeURIComponent(shard.handle)}.js`, fresh)
      ]);
      if (revision !== this.revision || this.destroyed) throw new Error('Hardware selections changed. Review the current setup and try again.');
      if (native.handle !== product.handle || carrier.handle !== shard.handle) throw new Error('The current hardware products could not be confirmed. Please refresh and review your setup.');
      const finishes = native.variants?.filter(variant => variant.sku === source.kaliaSku && variant.option1 === state.color && variant.price === source.priceCents && variant.available === true && Number.isSafeInteger(variant.id) && variant.id > 0);
      if (finishes?.length !== 1) throw new Error('The selected hardware finish, base price or availability has changed. Review your setup.');
      const variants = carrier.variants?.filter(variant => variant.sku === `KALIA-HW-P-${quote.unitCents}` && variant.option1 === `C${quote.unitCents}` && variant.price === quote.unitCents && variant.available === true && Number.isSafeInteger(variant.id) && variant.id > 0);
      if (variants?.length !== 1) throw new Error('The complete hardware price or availability has changed. Review your setup.');
      const variant = variants[0], summary = this.summary(product, state, quote), roomLabel = String(room || this.context.room || '').trim().slice(0, 120);
      const group = `hardware-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
      const props = {
        'Product title': product.title, 'Finish / type': state.color, 'Order unit': product.packUnit || 'Piece',
        'Room / project': roomLabel, 'Compatibility checked': 'Yes', 'Drapery heading': this.context.heading,
        '_Kalia hardware': 'yes', '_Kalia hardware kind': 'custom', '_Kalia hardware companion': '',
        '_Kalia hardware fit confirmed': state.confirmed ? 'yes' : 'no', '_Kalia companion key': '',
        '_Kalia group': group, '_Kalia role': 'product', '_Kalia charge required': 'no',
        '_Kalia parent variant': String(variant.id), '_Kalia configured title': product.title,
        '_Kalia configured image': imageUrl(source.image), '_Kalia configured url': this.rootUrl + `products/${product.handle}`,
        '_Kalia complete price': 'yes', '_Kalia hardware source': product.sourceHandle, '_Kalia unit cents': String(quote.unitCents)
      };
      if (bundleId) props['_Kalia pairing'] = String(bundleId).slice(0, 150);
      product.configuration.optionGroups.forEach(group => { const value = group.values.find(item => item.id === state.options[group.id]); if (value) props[group.label] = value.label; });
      props['Hardware width'] = `${quote.lengthInches} in`;
      if (state.segmentsInches) props['Coverage segments'] = state.segmentsInches.map((value, index) => `${['X', 'Y', 'Z'][index]} ${value} in`).join(' · ');
      props['Production unit'] = 'cm';
      props['Factory hardware width (cm)'] = cmDisplay(state.measurements.reduce((total, record) => total + record.cmMicros, 0));
      props['Customer hardware measurements'] = state.measurements.map((record, index) => `${state.segmentsInches ? ['X', 'Y', 'Z'][index] + ': ' : ''}${sourceMeasurement(record)}`).join(' · ');
      if (state.segmentsInches) props['Factory coverage segments (cm)'] = state.measurements.map((record, index) => `${['X', 'Y', 'Z'][index]} ${cmDisplay(record.cmMicros)} cm`).join(' · ');
      return { selected: true, totalCents: quote.totalCents, unitCents: quote.unitCents, summary, items: [{ id: variant.id, quantity: state.quantity, properties: props }] };
    }

    quote(options = {}) { return this.resolveQuote({ ...options, requireConfirmation: true }); }

    notify(value) { if (typeof this.settings.onChange === 'function') this.settings.onChange(value); }

    async refresh() {
      if (!this.status || this.destroyed) return;
      const revision = this.revision;
      if (!this.selection.selected) {
        this.price.textContent = ''; this.status.textContent = 'Hardware is optional and priced separately.';
        this.notify({ selected: false, ready: true, loading: false, totalCents: 0, summary: [], error: '' });
        return;
      }
      this.price.textContent = ''; this.status.textContent = 'Choose your setup to see the complete hardware price.';
      this.notify({ selected: true, ready: false, loading: true, totalCents: 0, summary: [], error: '' });
      try {
        const result = await this.resolveQuote({ requireConfirmation: false });
        if (revision !== this.revision || this.destroyed) return;
        this.price.textContent = `Hardware total ${this.money(result.totalCents)}`;
        this.status.textContent = this.selection.confirmed ? 'Hardware price and availability confirmed. Your panels and hardware keep separate quantities.' : 'Review your hardware measurements and fit, then confirm above.';
        this.notify({ selected: true, ready: this.selection.confirmed, loading: false, totalCents: result.totalCents, unitCents: result.unitCents, summary: result.summary, error: '' });
      } catch (error) {
        if (revision !== this.revision || this.destroyed) return;
        this.status.textContent = error.message;
        this.notify({ selected: true, ready: false, loading: false, totalCents: 0, summary: [], error: error.message });
      }
    }

    setDisabled(disabled) {
      this.disabled = Boolean(disabled);
      if (!this.fieldset) return;
      this.fieldset.disabled = this.disabled;
      this.body.querySelectorAll('input, select, textarea, button').forEach(input => { input.disabled = this.disabled || !this.selection.selected || input.kdhOptionUnavailable === true; });
      if (this.enableInput) this.enableInput.disabled = this.disabled || !this.initialized;
    }

    destroy() { this.destroyed = true; this.revision++; this.cache.clear(); }
  }

  window.KaliaDraperyHardware = KaliaDraperyHardware;
})();
